
'use client'

import Image from 'next/image'
import GradientText from './GradientText'

export default function HowItWorks() {

  const steps = [
    {
      number: "1",
      title: "Define Your Scenario",
      description: "Start with a simple prompt. \"Launch a SaaS for course creators\" or \"Expand our cybersecurity firm into the EU market.\""
    },
    {
      number: "2", 
      title: "Analyze Your Canvas",
      description: "Our AI generates your strategic Business Canvas, giving you a complete overview of the ecosystem you're about to enter."
    },
    {
      number: "3",
      title: "Execute Your Game Plan", 
      description: "Choose a Winning Game Plan and launch with the confidence of a strategy that has been battle-tested and optimized."
    }
  ]

  return (
    <section id="how-it-works" className="relative py-12 sm:py-20 lg:py-24" style={{ backgroundColor: '#001223' }}>
      <div className="relative z-10">
        <div className="container-stripe">
            {/* Section Header */}
            <div className="text-center mb-12 sm:mb-20 px-4 sm:px-0">
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-light mb-8 sm:mb-12 text-balance text-center max-w-5xl mx-auto text-white leading-relaxed drop-shadow-lg tracking-tight">
                From Ambition to Action in{' '}
                <GradientText colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}>
                  3 Steps
                </GradientText>
              </h2>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-12 sm:mb-16 px-4 sm:px-0">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start text-left"
                >
                  {/* Image */}
                  <div className="mb-6 sm:mb-8 w-full">
                    {index === 0 ? (
                      <div className="w-full aspect-[3/2] rounded-stripe-lg overflow-hidden relative">
                        <Image 
                          src="/images/scenario.png" 
                          alt="Define your business scenario with AI assistance"
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                        {/* Radial overlay */}
                        <div 
                          className="absolute inset-0 rounded-stripe-lg"
                          style={{
                            background: 'radial-gradient(circle at center, transparent 0%, transparent 30%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.6) 100%)'
                          }}
                        ></div>
                        {/* Scenario Icon */}
                        <svg className="absolute top-4 left-4 w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    ) : index === 1 ? (
                      <div className="w-full aspect-[3/2] rounded-stripe-lg overflow-hidden relative">
                        <Image 
                          src="/images/analyze.png" 
                          alt="Analyze your business canvas with comprehensive market insights"
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                        {/* Radial overlay */}
                        <div 
                          className="absolute inset-0 rounded-stripe-lg"
                          style={{
                            background: 'radial-gradient(circle at center, transparent 0%, transparent 30%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.6) 100%)'
                          }}
                        ></div>
                        {/* Canvas Icon */}
                        <svg className="absolute top-4 left-4 w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-full aspect-[3/2] rounded-stripe-lg overflow-hidden relative">
                        <Image 
                          src="/images/execute.png" 
                          alt="Execute your winning game plan with confidence"
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                        {/* Radial overlay */}
                        <div 
                          className="absolute inset-0 rounded-stripe-lg"
                          style={{
                            background: 'radial-gradient(circle at center, transparent 0%, transparent 30%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.6) 100%)'
                          }}
                        ></div>
                        {/* Game Plan Icon */}
                        <svg className="absolute top-4 left-4 w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Step Content */}
                  <div className="flex-1 ml-0 sm:ml-2">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-normal mb-4 sm:mb-6 text-white leading-tight tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg font-light text-blue-100/95 leading-relaxed drop-shadow-sm mb-6 sm:mb-8">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
        </div>
      </div>
    </section>
  )
}
